"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import type { Route } from "next";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

import { trackSearchSubmitted } from "@/lib/analytics";
import { useGhostAutocomplete } from "@/lib/hooks/useGhostAutocomplete";
import { GhostLayer } from "@/components/search/GhostLayer";
import { MOTION, ms } from "@/lib/design-tokens";
import { cn } from "@/lib/utils";

// Feature flag (B-117, 2026-06-18). Default OFF. Flip to "true" in
// .env.local / DO env vars to enable inline-ghost autocomplete on
// the homepage search box. Empty / unset value = OFF (the original
// no-autocomplete behaviour). Telemetry-driven rollout per the
// proposal's risk plan.
const AUTOCOMPLETE_ENABLED =
  process.env.NEXT_PUBLIC_AUTOCOMPLETE_ENABLED === "true";

// Static placeholder matching the mockup C contract. Rotation removed
// 2026-06-08: the redesign prefers a calm, single, locked prompt over
// a rotating set so the page never reads as busy on first paint.
const PLACEHOLDER = "What are you buying today?";

/**
 * Homepage search input. The single primary surface.
 *
 * Submit:
 *   Enter or click the amber CTA → routes to /compare?q=<query>. The
 *   redesigned compare page (Wave C/D) handles intent parse +
 *   clarification chips + paid search.
 *
 * Cmd+K (or Ctrl+K) focuses the input from anywhere on the page. The
 * visible hint text under the input was removed 2026-06-08 per the
 * mockup C redesign — the keyboard shortcut still works, it's just
 * not advertised to consumer visitors (it was reading as a
 * developer-tool hint, not a product feature).
 *
 * Visual contract: C:/tmp/mio-mockup-C.html lines 73-100 (signed off
 * 2026-06-08). 660px max-width, 20px border-radius, 56x56 amber
 * gradient button, backdrop blur(24px), shadow 0 24px 64px black/50.
 */
export function HomeSearchInput({ initialValue = "" }: { initialValue?: string }) {
  const router = useRouter();
  const [value, setValue] = useState(initialValue);
  const [isPending, startTransition] = useTransition();
  const inputRef = useRef<HTMLInputElement>(null);
  // B-117 (2026-06-18): inline-ghost autocomplete hook. When the
  // AUTOCOMPLETE_ENABLED flag is false the hook short-circuits and
  // returns an empty ghost on every render — zero rendering or
  // network impact.
  const auto = useGhostAutocomplete(value, setValue, {
    enabled: AUTOCOMPLETE_ENABLED,
  });

  // Cmd+K / Ctrl+K to focus.
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  function submit(query: string) {
    const q = query.trim();
    if (!q || isPending) return;
    trackSearchSubmitted({ query: q, source: "home" });
    startTransition(() => {
      router.push(`/compare?q=${encodeURIComponent(q)}` as Route);
    });
  }

  return (
    <motion.form
      onSubmit={(e) => {
        e.preventDefault();
        submit(value);
      }}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: ms(MOTION.smooth),
        ease: [0.16, 1, 0.3, 1],
        delay: 0.1,
      }}
      className="relative mx-auto w-full"
      style={{ maxWidth: 660 }}
    >
      {/* Glass-card input surface — mockup C lines 73-100 */}
      <div
        className={cn(
          "group relative flex items-center gap-3",
          "border bg-white/[0.05] backdrop-blur-2xl",
          "transition-shadow duration-200",
        )}
        style={{
          borderColor: "rgba(255,255,255,0.12)",
          borderRadius: 20,
          // 2026-08-10: left padding scales down on phones so the full
          // "What are you buying today?" placeholder fits; caps at 24px above
          // ~480px viewport, so tablet/desktop are unchanged.
          padding: "6px 6px 6px clamp(16px, 5vw, 24px)",
          boxShadow:
            "0 24px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        <span
          aria-hidden="true"
          className="flex-shrink-0 text-accent"
          style={{ fontSize: "1.25rem", lineHeight: 1 }}
        >
          ✦
        </span>
        <span style={{ position: "relative", display: "flex", flex: 1, minWidth: 0, alignItems: "center" }}>
          <GhostLayer
            userValue={value}
            suffix={auto.ghost}
            inputStyle={{
              fontSize: "clamp(14px, 4vw, 18px)",
              padding: "20px 4px",
              letterSpacing: "-0.005em",
            }}
          />
          <input
            ref={inputRef}
            type="text"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={auto.onKeyDown}
            onCompositionStart={auto.onCompositionStart}
            onCompositionEnd={auto.onCompositionEnd}
            placeholder={PLACEHOLDER}
            disabled={isPending}
            spellCheck={false}
            autoComplete="off"
            className={cn(
              "flex-1 bg-transparent mio-search-input",
              "text-foreground",
              "outline-none focus-visible:outline-none",
              "disabled:opacity-50",
              "relative",
            )}
            style={{
              // 2026-08-10: font scales down on phones so the placeholder is
              // never clipped; caps at 18px above ~450px viewport (desktop
              // unchanged). Kept in sync with the GhostLayer above.
              fontSize: "clamp(14px, 4vw, 18px)",
              padding: "20px 4px",
              letterSpacing: "-0.005em",
              background: "transparent",
            }}
            aria-label="What do you want to buy?"
          />
        </span>
        <button
          type="submit"
          disabled={!value.trim() || isPending}
          className={cn(
            "flex flex-shrink-0 items-center justify-center font-bold",
            "transition-transform duration-150",
            "hover:scale-[1.04] active:scale-[0.97]",
            "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100",
          )}
          style={{
            background:
              "linear-gradient(135deg, #fde68a 0%, #fbbf24 100%)",
            color: "#0a0908",
            width: 56,
            height: 56,
            borderRadius: 14,
            boxShadow: "0 6px 16px rgba(251,191,36,0.3)",
          }}
          aria-label={isPending ? "Searching" : "Search"}
        >
          {isPending ? (
            <span
              aria-hidden="true"
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
            />
          ) : (
            <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
          )}
        </button>
      </div>
    </motion.form>
  );
}
